import { Mic, MicOff } from "lucide-react";
import { useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "../../Services/Store/Store";
import {
  setConnecting,
  setConnected,
  setMuted,
  setError as setAgentError,
  setSessionData,
  updateTokenUsage,
} from "../../Services/Store/Slice/agentSlice";
import {
  useEditVenueSessionMutation,
  useCreateVenueMutation,
  useUpdateVenueMutation,
  useTrackTokensMutation,
  useGetTokenStatusQuery,
} from "../../Services/Store/Slice/agentApi";

interface ApiError {
  // `message` is the user-facing sentence; `error` is the short label.
  data?: { error?: string; message?: string; code?: string };
  message?: string;
}

interface VenueContext {
  id: string;
  name?: string;
  mode: "create" | "update";
}

interface VenueAgentProps {
  venueId?: string;
  onVenueCreated?: (venue: any) => void;
  onVenueUpdated?: (venue: any) => void;
}

export const VenueAgent: React.FC<VenueAgentProps> = ({
  venueId,
  onVenueCreated,
  onVenueUpdated,
}) => {
  const dispatch = useDispatch();
  const agentState = useSelector((state: RootState) => state.agent);

  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const dataChannelRef = useRef<RTCDataChannel | null>(null);
  const audioElementRef = useRef<HTMLAudioElement>(new Audio());
  const mediaStreamRef = useRef<MediaStream | null>(null);

  const venueContextRef = useRef<VenueContext>({
    id: venueId || "",
    mode: venueId ? "update" : "create",
  });

  const [editVenueSession] = useEditVenueSessionMutation();
  const [createVenue] = useCreateVenueMutation();
  const [updateVenue] = useUpdateVenueMutation();
  const [trackTokens] = useTrackTokensMutation();
  useGetTokenStatusQuery(undefined);

  const pcConfig = {
    iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
  };

  const updateVenueContext = useCallback((venueData: any) => {
    venueContextRef.current = {
      id: venueData.id,
      name: venueData.name || venueData.title,
      mode: "update",
    };
    console.log("Updated venue context:", venueContextRef.current);
  }, []);

  const sendContextUpdate = useCallback(() => {
    if (dataChannelRef.current?.readyState === "open") {
      const context = venueContextRef.current;
      const contextMessage =
        context.mode === "update"
          ? `Context Update: You are now in UPDATE mode for venue "${
              context.name || context.id
            }" (ID: ${context.id}). Use this venue ID for all future updates.`
          : `Context Update: You are in CREATE mode. No venue exists yet.`;

      dataChannelRef.current.send(
        JSON.stringify({
          type: "conversation.item.create",
          item: {
            type: "message",
            role: "system",
            content: [
              {
                type: "text",
                text: contextMessage,
              },
            ],
          },
        })
      );
    }
  }, []);

  const handleFunctionCall = useCallback(
    async (callId: string, functionName: string, args: string) => {
      try {
        console.log(`Executing function: ${functionName} with args:`, args);

        const parsedArgs = JSON.parse(args);
        let result;

        switch (functionName) {
          case "create": {
            try {
              if (venueContextRef.current.mode === "update") {
                result = {
                  error:
                    "Cannot create a new venue when already in update mode. Use update function instead.",
                };
                break;
              }

              const createResult = await createVenue(parsedArgs).unwrap();
              console.log("Venue created:", createResult);

              updateVenueContext(createResult.data);

              if (onVenueCreated) {
                onVenueCreated(createResult.data);
              }

              result = {
                success: true,
                message:
                  "Venue created successfully! You can now update this venue.",
                venue: createResult.data,
                venueId: createResult.data.id,
                contextUpdate: `Switched to UPDATE mode for venue "${
                  createResult.data.name || createResult.data.title
                }" (ID: ${createResult.data.id})`,
              };

              setTimeout(() => sendContextUpdate(), 100);
            } catch (error: any) {
              result = {
                error: error.data?.message || "Failed to create venue",
              };
            }
            break;
          }

          case "update": {
            try {
              const venueIdToUpdate =
                parsedArgs.id || venueContextRef.current.id;

              if (!venueIdToUpdate) {
                result = {
                  error:
                    "No venue ID available for update. Please create a venue first.",
                };
                break;
              }

              const updateArgs = { ...parsedArgs, id: venueIdToUpdate };
              const updateResult = await updateVenue(updateArgs).unwrap();

              updateVenueContext(updateResult.data);

              // Call the callback to update the form
              if (onVenueUpdated) {
                onVenueUpdated(updateResult.data);
              }

              result = {
                success: true,
                message: "Venue updated successfully!",
                venue: updateResult.data,
                venueId: venueIdToUpdate,
              };
            } catch (error: any) {
              result = {
                error: error.data?.message || "Failed to update venue",
              };
            }
            break;
          }

          default: {
            result = { error: `Unknown function: ${functionName}` };
            break;
          }
        }

        if (dataChannelRef.current?.readyState === "open") {
          dataChannelRef.current.send(
            JSON.stringify({
              type: "conversation.item.create",
              item: {
                type: "function_call_output",
                call_id: callId,
                output: JSON.stringify(result),
              },
            })
          );
        }
      } catch (err) {
        console.error("Function call error:", err);
        if (dataChannelRef.current?.readyState === "open") {
          dataChannelRef.current.send(
            JSON.stringify({
              type: "conversation.item.create",
              item: {
                type: "function_call_output",
                call_id: callId,
                output: JSON.stringify({ error: "Function execution failed" }),
              },
            })
          );
        }
      }
    },
    [
      createVenue,
      updateVenue,
      updateVenueContext,
      sendContextUpdate,
      onVenueCreated,
      onVenueUpdated,
    ]
  );

  const handleTokenUsage = useCallback(
    async (tokensUsed: number) => {
      try {
        await trackTokens({ tokensUsed }).unwrap();
        dispatch(updateTokenUsage(tokensUsed));

        if (agentState.tokenStatus) {
          const newRemaining = agentState.tokenStatus.remaining - tokensUsed;
          if (newRemaining <= 0) {
            handleDisconnect();
            dispatch(setAgentError("Token limit reached. Session ended."));
          }
        }
      } catch (err) {
        console.error("Token tracking error:", err);
      }
    },
    [trackTokens, dispatch, agentState.tokenStatus]
  );

  const stopMediaStream = useCallback(() => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => {
        track.stop();
        console.log(`Stopped ${track.kind} track`);
      });
      mediaStreamRef.current = null;
    }
  }, []);

  const setupWebRTC = useCallback(
    async () => {
      try {
        const pc = new RTCPeerConnection(pcConfig);
        peerConnectionRef.current = pc;

        if (!audioElementRef.current) {
          audioElementRef.current = new Audio();
        }
        audioElementRef.current.autoplay = true;

        pc.ontrack = (event) => {
          console.log("Received remote audio track");
          if (audioElementRef.current) {
            audioElementRef.current.srcObject = event.streams[0];
          }
        };

        const dataChannel = pc.createDataChannel("oai-events");
        dataChannelRef.current = dataChannel;

        dataChannel.onopen = () => {
          const context = venueContextRef.current;

          const modeInstructions =
            context.mode === "update"
              ? `You are in UPDATE mode for venue "${
                  context.name || context.id
                }" (ID: ${context.id}). 
               Greet the user and let them know you're ready to help update their existing venue. 
               Ask what changes they'd like to make to their venue.`
              : `You are in CREATE mode. 
               Greet the user warmly and let them know you're ready to help create a new venue. 
               Ask them to describe the venue they'd like to create.`;

          dataChannel.send(
            JSON.stringify({
              type: "response.create",
              response: {
                modalities: ["audio", "text"],
                instructions: `${modeInstructions}

Key Instructions:
- Be warm, friendly, and proactive
- Keep responses concise but helpful
- Always confirm successful operations
- If in CREATE mode and venue is created successfully, remember you're now in UPDATE mode
- If in UPDATE mode, always use the venue ID: ${context.id}
- When collecting venue information, also ask about area (in square feet/meters) and architectural styles

Start speaking immediately and be ready to assist!`,
              },
            })
          );
        };

        dataChannel.onmessage = async (event) => {
          try {
            const message = JSON.parse(event.data);
            console.log("Received message:", message.type);

            if (message.type === "response.function_call_arguments.done") {
              await handleFunctionCall(
                message.call_id,
                message.name,
                message.arguments
              );
            }

            if (message.type === "response.done" && message.response?.usage) {
              await handleTokenUsage(message.response.usage.total_tokens || 0);
            }

            if (message.type === "session.created") {
              console.log("Session created successfully");
            }

            if (message.type === "conversation.item.created") {
              console.log("Conversation item created");
            }
          } catch (err) {
            console.error("Error processing message:", err);
          }
        };

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            sampleRate: 24000,
          },
        });

        mediaStreamRef.current = stream;
        console.log(
          "Got user media stream with tracks:",
          stream.getTracks().length
        );

        stream.getTracks().forEach((track) => {
          pc.addTrack(track, stream);
          console.log(`Added ${track.kind} track to peer connection`);
        });

        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        console.log("Created and set local offer");

        // GA Realtime: our backend combines this offer with the session
        // config and posts both to /v1/realtime/calls using the standing
        // server key. No OpenAI credential ever reaches the browser.
        const sessionResult = await editVenueSession({
          sdp: offer.sdp!,
          venueId: venueContextRef.current.id || undefined,
        }).unwrap();

        await pc.setRemoteDescription({
          type: "answer",
          sdp: sessionResult.data.sdp,
        });
        console.log("Set remote description");

        return sessionResult;
      } catch (err) {
        console.error("WebRTC setup error:", err);
        stopMediaStream();
        throw err;
      }
    },
    [editVenueSession, handleFunctionCall, handleTokenUsage, stopMediaStream]
  );

  const handleConnect = useCallback(async () => {
    try {
      dispatch(setConnecting(true));
      console.log("Starting connection...");

      // setupWebRTC now owns the whole negotiation: it builds the offer,
      // sends it to our backend (which creates the paid Realtime call) and
      // applies the answer.
      const sessionResult = await setupWebRTC();
      dispatch(setSessionData(sessionResult.data));

      dispatch(setConnected(true));
      console.log("Successfully connected to voice agent");
    } catch (err: unknown) {
      console.error("Connection failed:", err);
      // Prefer the backend's user-facing sentence: it distinguishes the burst
      // limit from the hourly limit, and quota from provider failures, where
      // the short `error` label does not.
      const errorMessage =
        (err as ApiError).data?.message ||
        (err as ApiError).data?.error ||
        (err as ApiError).message ||
        "Connection failed";
      dispatch(setAgentError(errorMessage));

      stopMediaStream();
    } finally {
      dispatch(setConnecting(false));
    }
  }, [setupWebRTC, dispatch, stopMediaStream]);

  const handleDisconnect = useCallback(() => {
    console.log("Disconnecting voice agent...");

    stopMediaStream();

    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
      console.log("Closed peer connection");
    }

    if (dataChannelRef.current) {
      dataChannelRef.current.close();
      dataChannelRef.current = null;
      console.log("Closed data channel");
    }

    if (audioElementRef.current) {
      audioElementRef.current.pause();
      audioElementRef.current.srcObject = null;
      console.log("Stopped audio element");
    }

    dispatch(setConnected(false));
    dispatch(setSessionData(null));
    dispatch(setMuted(false));
  }, [dispatch, stopMediaStream]);

  const toggleMute = useCallback(() => {
    if (peerConnectionRef.current && mediaStreamRef.current) {
      const audioTracks = mediaStreamRef.current.getAudioTracks();
      audioTracks.forEach((track) => {
        track.enabled = agentState.isMuted;
      });

      dispatch(setMuted(!agentState.isMuted));
      console.log(`Microphone ${agentState.isMuted ? "unmuted" : "muted"}`);
    }
  }, [agentState.isMuted, dispatch]);

  useEffect(() => {
    const newMode = venueId ? "update" : "create";
    venueContextRef.current = {
      id: venueId || "",
      mode: newMode,
    };
    console.log("Venue context updated from props:", venueContextRef.current);
  }, [venueId]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (agentState.isConnected) {
        event.preventDefault();
        event.returnValue =
          "You are currently connected to the AI agent. Are you sure you want to leave?";
        return event.returnValue;
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden && agentState.isConnected) {
        handleDisconnect();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      if (agentState.isConnected) handleDisconnect();
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [agentState.isConnected, handleDisconnect]);

  useEffect(() => {
    if (agentState.error) {
      const timer = setTimeout(() => dispatch(setAgentError("")), 5000);
      return () => clearTimeout(timer);
    }
  }, [agentState.error, dispatch]);


  return (
    <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-4 mx-auto text-center shadow-sm border border-white/20">
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2">Venue Agent</h1>
      </div>

      {agentState.tokenStatus && (
        <div className="mb-6 p-4 bg-white/5 rounded-xl border border-white/10">
          <div className="text-sm text-gray-700 mb-2">Token Usage</div>
          <div className="flex justify-between text-black mb-2">
            <span>Remaining:</span>
            <span
              className={`font-mono ${
                agentState.tokenStatus.remaining < 1000
                  ? "text-red-600"
                  : "text-green-600"
              }`}
            >
              {agentState.tokenStatus.remaining.toLocaleString()}
            </span>
          </div>
          <div className="w-full bg-gray-700 rounded-full h-2 mb-2">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${
                agentState.tokenStatus.remaining < 1000
                  ? "bg-red-500"
                  : agentState.tokenStatus.remaining < 10000
                  ? "bg-yellow-500"
                  : "bg-green-500"
              }`}
              style={{
                width: `${
                  (agentState.tokenStatus.remaining /
                    agentState.tokenStatus.total) *
                  100
                }%`,
              }}
            />
          </div>
          <div className="text-xs text-gray-600">
            {/* `expiresAt` is a legacy field that no longer governs access,
                so showing a past expiry date would be untrue. */}
            {agentState.tokenStatus.used.toLocaleString()} of{" "}
            {agentState.tokenStatus.total.toLocaleString()} used
          </div>
        </div>
      )}

      {agentState.error && (
        <div className="mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-xl">
          <p className="text-red-200 text-sm">{agentState.error}</p>
        </div>
      )}

      <div className="mb-4">
        <div
          className={`w-4 h-4 rounded-full mx-auto mb-4 ${
            agentState.isConnected
              ? "bg-green-500 animate-pulse"
              : agentState.isConnecting
              ? "bg-yellow-500 animate-pulse"
              : "bg-gray-500"
          }`}
        />
        <p className="text-black text-sm">
          {agentState.isConnected
            ? "Connected - AI can hear you"
            : agentState.isConnecting
            ? "Connecting..."
            : "Disconnected"}
        </p>
        {agentState.isConnected && (
          <p className="text-gray-600 text-xs mt-1">
            Microphone is {agentState.isMuted ? "muted" : "active"}
          </p>
        )}
      </div>

      <div className="flex justify-center items-center gap-2">
        <button
          onClick={agentState.isConnected ? handleDisconnect : handleConnect}
          disabled={agentState.isConnecting}
          className={`rounded-full p-6 font-semibold text-lg transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:scale-100 ${
            agentState.isConnected
              ? "bg-red-500 hover:bg-red-600 text-black shadow-lg shadow-red-500/30"
              : "bg-green-500 hover:bg-green-600 text-black shadow-lg shadow-green-500/30"
          }`}
        >
          <div className="flex items-center justify-center">
            {agentState.isConnected ? <MicOff size={24} /> : <Mic size={24} />}
          </div>
        </button>
        {agentState.isConnected && (
          <button
            onClick={toggleMute}
            className={`p-3 my-auto rounded-full font-medium transition-all duration-300 transform hover:scale-105 ${
              agentState.isMuted
                ? "bg-orange-500 hover:bg-orange-600 text-black shadow-lg shadow-orange-500/30"
                : "bg-gray-500 hover:bg-gray-600 text-black shadow-lg shadow-blue-500/30"
            }`}
          >
            <div className="flex items-center justify-center">
              <MicOff size={16} />
            </div>
          </button>
        )}
      </div>

      {agentState.tokenStatus && agentState.tokenStatus.remaining < 1000 && (
        <div className="mt-6 p-4 bg-yellow-500/20 border border-yellow-500/30 rounded-xl">
          <p className="text-yellow-200 text-sm">
            ⚠️ Low tokens remaining:{" "}
            {agentState.tokenStatus.remaining.toLocaleString()}
          </p>
        </div>
      )}
      
      <audio ref={audioElementRef} style={{ display: "none" }} />
    </div>
  );
};
